import clsx from 'clsx';
import type { ReactNode, RefObject } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { voidFunction } from '@repo/common';
import { Scroller } from '@repo/ui/components/elements/Scroller/Scroller';
import { useOutsideClick } from '@repo/ui/hooks/use-outside-click';

import { PortalType, useUiPortal } from '../../../contexts/ui-context';

import './ScrollerDropdown.scss';

interface ScrollerDropdownProps {
  anchorRef: RefObject<HTMLElement | null>;
  showed?: boolean;
  onClickOutside?: () => void;
  children: ReactNode;
  dataTestId?: string;
}

/**
 * Positions the floating menu with `position: fixed` using viewport coords from
 * getBoundingClientRect (no window.scrollY). Capture-phase scroll listeners keep
 * the menu glued when nested containers scroll (admin layout, tables, etc.).
 */
export function ScrollerDropdown({
  anchorRef,
  showed = false,
  onClickOutside = voidFunction,
  children,
  dataTestId,
}: ScrollerDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const portalContainer = useUiPortal(PortalType.Popup);
  const shouldRenderInline = !portalContainer?.isConnected;

  useOutsideClick([anchorRef, dropdownRef], onClickOutside);

  useLayoutEffect(() => {
    if (!showed) {
      return undefined;
    }

    const updatePosition = () => {
      if (!(anchorRef.current && dropdownRef.current)) {
        return;
      }
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const gap = 4;

      const fitsBelow = anchorRect.bottom + gap + dropdownRect.height <= viewportHeight;
      const top = fitsBelow ? anchorRect.bottom + gap : Math.max(0, anchorRect.top - dropdownRect.height - gap);
      const left = anchorRect.left;

      dropdownRef.current.style.top = `${top}px`;
      dropdownRef.current.style.left = `${left}px`;
      dropdownRef.current.style.width = `${anchorRect.width}px`;
    };

    updatePosition();
    let animationFrameId: number | undefined;
    const handleScrollOrResize = () => {
      if (animationFrameId !== undefined) {
        cancelAnimationFrame(animationFrameId);
      }
      animationFrameId = requestAnimationFrame(updatePosition);
    };

    const observer = new ResizeObserver(handleScrollOrResize);
    const dropdownElement = dropdownRef.current;
    if (dropdownElement) {
      observer.observe(dropdownElement);
    }
    if (anchorRef.current) {
      observer.observe(anchorRef.current);
    }

    // Capture phase catches scroll on any nested overflow container.
    document.addEventListener('scroll', handleScrollOrResize, { capture: true, passive: true });
    window.addEventListener('resize', handleScrollOrResize, { passive: true });

    return () => {
      if (animationFrameId !== undefined) {
        cancelAnimationFrame(animationFrameId);
      }
      document.removeEventListener('scroll', handleScrollOrResize, { capture: true });
      window.removeEventListener('resize', handleScrollOrResize);
      observer.disconnect();
    };
  }, [anchorRef, showed]);

  if (shouldRenderInline) {
    return (
      <div
        ref={dropdownRef}
        className={clsx('ScrollerDropdown', 'ScrollerDropdown--embedded', { 'is-open': showed })}
        data-testid={dataTestId}
      >
        <Scroller className="ScrollerDropdown__content">{children}</Scroller>
      </div>
    );
  }

  return createPortal(
    <div
      ref={dropdownRef}
      className={clsx('ScrollerDropdown', 'ScrollerDropdown--floating')}
      style={{
        display: showed ? 'block' : 'none',
        top: 0,
        left: 0,
        zIndex: portalContainer.style.zIndex,
        width: '100%',
      }}
      data-testid={dataTestId}
    >
      <Scroller className="ScrollerDropdown__content">{children}</Scroller>
    </div>,
    portalContainer,
  );
}
